import { ACTING_ROLE_LABELS, ACTING_ROLE_SHORT_LABELS, offerableRoles, type ActingRole } from '@brush/auth';
import type { RoleChoice } from '@brush/ui';

/**
 * The roles this app can do anything with.
 *
 * Player is deliberately absent. Everybody who signs in is a player, so the server offers it to
 * everyone — but this is the staff console, and choosing it here would lock somebody out of the
 * app they had just opened. The portal is where that role means something.
 *
 * Admin first, matching the order the server reports and the privilege order the API defaults to.
 */
export const SUPPORTED_ROLES: readonly ActingRole[] = ['admin', 'room'];

/**
 * What each role can do *here*, in the operator's terms rather than the schema's.
 *
 * The label alone does not carry the choice: somebody picking between "Platform administrator"
 * and "Room operator" is really choosing between every room and their own, and that is the
 * sentence that has to be on screen. The distinction is not cosmetic — acting as an operator
 * gives up the room-scope bypass, so an administrator who picks it is genuinely refused rooms
 * they hold no assignment for.
 */
const DESCRIPTIONS: Record<ActingRole, string> = {
  admin: 'Every room on the platform, and the settings behind them.',
  room: 'Only the rooms you are assigned to operate.',
  player: 'Your own results in the player portal.',
};

/** The roles to offer this account, or fewer than two when there is nothing to choose. */
export function roleChoicesFor(availableRoles: readonly string[]): RoleChoice[] {
  return offerableRoles(availableRoles, SUPPORTED_ROLES).map((role) => ({
    id: role,
    label: ACTING_ROLE_LABELS[role],
    description: DESCRIPTIONS[role],
  }));
}

export function shortLabelFor(role: ActingRole): string {
  return ACTING_ROLE_SHORT_LABELS[role];
}

/**
 * Shown when a stored choice is no longer accepted — the server answered
 * `acting_role_not_held`, which happens when a room assignment is withdrawn between
 * sessions. Without saying so the chooser reappearing reads as the app having forgotten who you
 * are, which sends somebody to sign out and back in for no reason.
 */
export const STALE_ROLE_NOTICE =
  'The role you were using is no longer available to your account. Choose another to carry on.';

/**
 * Shown to somebody acting as an operator who operates no room, when they hold another role to
 * fall back on. Almost always an administrator who has just chosen Operator: the app is empty
 * because of the choice rather than because of the account, and saying "ask a platform
 * administrator" to a platform administrator is the wrong instruction twice over.
 */
export const UNASSIGNED_OPERATOR_NOTICE =
  "You're acting as a room operator, but your account isn't assigned to any room. Choose another role to carry on.";
